import {
  Building2,
  HelpCircle,
  LogOut,
  Megaphone,
  MessageSquare,
  Plus,
  Settings,
  Store,
  User as UserIcon,
  X,
} from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { useLogoutMutation } from '@/features/auth/hooks/useLogoutMutation'
import { useCanManageComms } from '@/features/communications/hooks/useCanManageComms'
import { Avatar } from '@/shared/components/Avatar'
import { useModalA11y } from '@/shared/components/modal/useModalA11y'
import { useLocalizedHref } from '@/shared/hooks/useLocalizedHref'
import { cn } from '@/shared/lib/cn'

interface AccountMenuProps {
  onClose: () => void
}

/**
 * Sheet brutal lanzado desde el tab "Cuenta" del MobileTabBar.
 *
 * Reemplaza al antiguo MobileMenu (que duplicaba la navegación principal).
 * El sheet se enfoca solo en acciones de cuenta:
 *
 * - Autenticado: bloque usuario (avatar + nombre) + Mi perfil / Mis mensajes
 *   (disabled) / Ayuda / Cerrar sesión (red).
 * - Anónimo: pitch breve + Iniciar sesión / Crear cuenta.
 *
 * Cierra con Esc, click fuera (backdrop) y cambio de ruta. `useModalA11y`
 * centraliza Esc, scroll-lock, focus-trap dentro del sheet y la devolución del
 * foco al tab que lo abrió. `md:hidden` — solo mobile.
 */
export function AccountMenu({ onClose }: AccountMenuProps) {
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuth()
  const canManageComms = useCanManageComms()
  const logout = useLogoutMutation()
  const location = useLocation()
  const helpHref = useLocalizedHref('help-hub')

  // Cierra al cambiar de ruta (excepto en el mount inicial)
  const initialPath = useRef(location.pathname)
  useEffect(() => {
    if (location.pathname !== initialPath.current) onClose()
  }, [location.pathname, onClose])

  // Montado = abierto (el padre lo desmonta al cerrar). El trap va sobre el sheet,
  // no sobre el backdrop (que es un botón solo para ratón/touch).
  const sheetRef = useRef<HTMLDivElement>(null)
  useModalA11y(sheetRef, { onClose })

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.accountMenuLabel')}
      className="fixed inset-0 z-50 flex flex-col justify-end md:hidden"
    >
      {/* Backdrop semi-transparente clickable para cerrar */}
      <button
        type="button"
        aria-label={t('nav.closeMenu')}
        onClick={onClose}
        className="absolute inset-0 bg-foreground/40"
      />

      {/* Sheet brutal — slide-up desde abajo */}
      <div
        ref={sheetRef}
        className="brutal-lg relative max-h-[85vh] overflow-y-auto overscroll-contain rounded-t-2xl bg-background pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
      >
        {/* Cabecera del sheet con X */}
        <div className="flex items-center justify-between border-b-2 border-foreground/10 px-5 pb-3 pt-5">
          <h2 className="text-balance font-display text-lg font-black">
            {t('nav.accountMenuTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav.closeMenu')}
            className="brutal-sm inline-flex size-9 items-center justify-center rounded-md bg-background"
          >
            <X size={18} strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>

        {isAuthenticated && user ? (
          <>
            {/* Bloque usuario */}
            <div className="flex items-center gap-3 px-5 py-4">
              <Avatar
                username={user.username}
                avatarCode={user.selectedAvatarCode}
                size={48}
                ringBrutal
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-black">@{user.username}</p>
                <p className="font-brutal text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {t('nav.signedInAs')}
                </p>
              </div>
            </div>

            {/* Items */}
            <nav className="flex flex-col gap-2 px-5" aria-label={t('nav.accountMenuLabel')}>
              {user.role !== 'TIENDA' && (
                <AccountItem
                  to="/profile"
                  icon={<UserIcon size={20} aria-hidden="true" />}
                  accent="blue"
                >
                  {t('nav.profile')}
                </AccountItem>
              )}
              {/* "Mensajes" = chat de sesiones. Una tienda no juega, así que
                  no tiene mensajes. */}
              {user.role !== 'TIENDA' && (
                <AccountItem
                  to="/messages"
                  icon={<MessageSquare size={20} aria-hidden="true" />}
                  accent="blue"
                >
                  {t('nav.messages')}
                </AccountItem>
              )}
              {user.role === 'TIENDA' && (
                <AccountItem
                  to="/tienda"
                  icon={<Store size={20} aria-hidden="true" />}
                  accent="green"
                >
                  {t('nav.myStore')}
                </AccountItem>
              )}
              {user.role === 'TIENDA' && (
                <AccountItem
                  to="/tienda/ajustes"
                  icon={<Settings size={20} aria-hidden="true" />}
                  accent="green"
                >
                  {t('nav.storeSettings')}
                </AccountItem>
              )}
              {canManageComms && (
                <AccountItem
                  to="/comunicaciones"
                  icon={<Megaphone size={20} aria-hidden="true" />}
                  accent="red"
                >
                  {t('nav.communications')}
                </AccountItem>
              )}
              <AccountItem
                to="/locales"
                icon={<Building2 size={20} aria-hidden="true" />}
                accent="green"
              >
                {t('nav.venues')}
              </AccountItem>
              <AccountItem
                to={helpHref}
                icon={<HelpCircle size={20} aria-hidden="true" />}
                accent="yellow"
              >
                {t('nav.help')}
              </AccountItem>
            </nav>

            {/* Logout */}
            <div className="mt-4 px-5">
              <button
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                className="brutal brutal-press flex w-full items-center justify-center gap-2 rounded-xl bg-red px-4 py-3.5 font-display text-sm font-bold text-background disabled:opacity-60"
              >
                <LogOut size={16} strokeWidth={2.5} aria-hidden="true" />
                {t('nav.logout')}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Pitch anónimo */}
            <div className="px-5 pt-4">
              <p className="font-display text-base font-bold leading-snug">
                {t('nav.accountMenuAnonPitch')}
              </p>
            </div>

            {/* Items anon */}
            <nav className="mt-4 flex flex-col gap-2 px-5" aria-label={t('nav.accountMenuLabel')}>
              <AccountItem
                to="/login"
                icon={<UserIcon size={20} aria-hidden="true" />}
                accent="blue"
              >
                {t('nav.login')}
              </AccountItem>
              <AccountItem to="/register" icon={<Plus size={20} aria-hidden="true" />} accent="red">
                {t('nav.register')}
              </AccountItem>
              <AccountItem
                to="/locales"
                icon={<Building2 size={20} aria-hidden="true" />}
                accent="green"
              >
                {t('nav.venues')}
              </AccountItem>
              <AccountItem
                to={helpHref}
                icon={<HelpCircle size={20} aria-hidden="true" />}
                accent="yellow"
              >
                {t('nav.help')}
              </AccountItem>
            </nav>
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AccountItem · row brutal con icono coloreado
// ---------------------------------------------------------------------------

type Accent = 'red' | 'yellow' | 'green' | 'blue'

const ACCENT_CLASSES: Record<Accent, { bg: string; text: string }> = {
  red: { bg: 'bg-red', text: 'text-background' },
  yellow: { bg: 'bg-yellow', text: 'text-foreground' },
  green: { bg: 'bg-green', text: 'text-background' },
  blue: { bg: 'bg-blue', text: 'text-background' },
}

interface AccountItemProps {
  to?: string
  disabled?: boolean
  icon: React.ReactNode
  accent: Accent
  badge?: string
  children: React.ReactNode
}

function AccountItem({ to, disabled, icon, accent, badge, children }: AccountItemProps) {
  const accentClasses = ACCENT_CLASSES[accent]

  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'brutal-sm inline-flex size-10 shrink-0 items-center justify-center rounded-lg',
          accentClasses.bg,
          accentClasses.text,
        )}
      >
        {icon}
      </span>
      <span className="font-display text-base font-bold">{children}</span>
      {badge && (
        <span className="brutal-sm ml-auto rounded-md bg-muted px-2 py-0.5 font-brutal text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {badge}
        </span>
      )}
    </>
  )

  const className = cn(
    'brutal flex items-center gap-3 rounded-xl px-3.5 py-3 transition-colors',
    !disabled && 'bg-background hover:bg-yellow/40',
    disabled && 'cursor-not-allowed bg-background opacity-55',
  )

  if (disabled || !to) {
    return (
      <div className={className} aria-disabled={disabled || undefined}>
        {content}
      </div>
    )
  }

  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  )
}
